import React, {useEffect, useState, useRef} from 'react';
import Spinner from 'react-bootstrap/Spinner';

/**
 * ExcelGrid – tabla editable estilo Excel.
 *
 * Props:
 *   rows      {Array<Object>}  – array de filas, cada fila es { [key]: value }
 *   colKeys   {Array<{key, label}>} – columnas a mostrar
 *   onChange  {Function}       – callback(newRows) cuando cambia cualquier celda
 *
 * Funcionalidades:
 *   - Edición celda a celda (click / Tab / Enter / Flechas)
 *   - Selección múltiple con mouse (drag) — resaltado azul
 *   - Ctrl+V: pega datos tabulados desde Excel a partir de la celda activa
 *   - Backspace / Delete sobre selección múltiple: limpia las celdas seleccionadas
 *   - Al salir de una celda, elimina filas completamente vacías (mínimo 1)
 *   - Al agregar filas (rows crece), el scroll baja automáticamente al final
 */
const ExcelGrid = ({rows, colKeys, onChange}) => {
  const [activeCell, setActiveCell] = useState(null); // {row, col}
  const [selecting, setSelecting] = useState(false);
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const gridRef = useRef(null);
  const prevRowCount = useRef(rows.length);

  // Auto-scroll al fondo cuando se agregan filas
  useEffect(() => {
    if (rows.length > prevRowCount.current && gridRef.current) {
      gridRef.current.scrollTop = gridRef.current.scrollHeight;
    }
    prevRowCount.current = rows.length;
  }, [rows.length]);

  const isSelected = (r, c) => {
    if (!selStart || !selEnd) return false;
    const minR = Math.min(selStart.row, selEnd.row);
    const maxR = Math.max(selStart.row, selEnd.row);
    const minC = Math.min(selStart.col, selEnd.col);
    const maxC = Math.max(selStart.col, selEnd.col);
    return r >= minR && r <= maxR && c >= minC && c <= maxC;
  };

  const handleCellChange = (rowIdx, key, val) => {
    const newRows = rows.map((r, i) => i === rowIdx ? {...r, [key]: val} : r);
    onChange(newRows);
  };

  const clearSelection = () => {
    if (!selStart || !selEnd) return;
    const minR = Math.min(selStart.row, selEnd.row);
    const maxR = Math.max(selStart.row, selEnd.row);
    const minC = Math.min(selStart.col, selEnd.col);
    const maxC = Math.max(selStart.col, selEnd.col);
    const newRows = rows.map((row, ri) => {
      if (ri < minR || ri > maxR) return row;
      const updated = {...row};
      colKeys.forEach((col, ci) => {
        if (ci >= minC && ci <= maxC && col.key !== 'original') updated[col.key] = '';
      });
      return updated;
    });
    onChange(newRows);
  };

  const focusCell = (r, c) => {
    const el = gridRef.current?.querySelector(`[data-r="${r}"][data-c="${c}"]`);
    if (el) el.focus();
  };

  const handleKeyDown = (e, rowIdx, colIdx) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const selSpan =
        selStart && selEnd &&
        (selStart.row !== selEnd.row || selStart.col !== selEnd.col);
      if (selSpan) {
        e.preventDefault();
        clearSelection();
        return;
      }
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      const nextCol = e.shiftKey ? colIdx - 1 : colIdx + 1;
      if (nextCol >= 0 && nextCol < colKeys.length) {
        focusCell(rowIdx, nextCol);
      } else if (!e.shiftKey && rowIdx < rows.length - 1) {
        focusCell(rowIdx + 1, 0);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx < rows.length - 1) focusCell(rowIdx + 1, colIdx);
    } else if (e.key === 'ArrowDown' && !e.shiftKey) {
      e.preventDefault();
      if (rowIdx < rows.length - 1) focusCell(rowIdx + 1, colIdx);
    } else if (e.key === 'ArrowUp' && !e.shiftKey) {
      e.preventDefault();
      if (rowIdx > 0) focusCell(rowIdx - 1, colIdx);
    }
  };

  const handlePaste = (e) => {
    const clipText = e.clipboardData.getData('Text');
    const lines = clipText.split('\n').map((l) => l.replace(/\r$/, '')).filter((l) => l !== '');
    if (!lines.length) return;
    const startRow = selStart ? selStart.row : 0;
    const startCol = selStart ? selStart.col : 0;
    e.preventDefault();
    const newRows = [...rows];
    lines.forEach((line, li) => {
      const cols = line.split('\t');
      const ri = startRow + li;
      if (ri >= newRows.length) newRows.push(Object.fromEntries(colKeys.map((c) => [c.key, ''])));
      cols.forEach((val, ci) => {
        const colIdx = startCol + ci;
        if (colIdx < colKeys.length) {
          const colKey = colKeys[colIdx].key;
          if (colKey !== 'original') {
            newRows[ri] = {...newRows[ri], [colKey]: val.trim()};
          }
        }
      });
    });
    onChange(newRows);
  };

  const ROW_H = 32;
  const maxH = Math.min(rows.length * ROW_H + ROW_H + 2, 480);

  return (
    <div
      ref={gridRef}
      onPaste={handlePaste}
      style={{
        overflowY: 'auto',
        overflowX: 'auto',
        maxHeight: `${maxH}px`,
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        userSelect: 'none',
      }}
    >
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          tableLayout: 'fixed',
          minWidth: `${colKeys.length * 160}px`,
        }}
      >
        <colgroup>
          <col style={{width: '36px'}} />
          {colKeys.map((_, i) => <col key={i} style={{width: '200px'}} />)}
        </colgroup>
        <thead>
          <tr>
            <th style={thRowNum}></th>
            {colKeys.map((c) => (
              <th key={c.key} style={thStyle}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              <td style={tdRowNum}>{ri + 1}</td>
              {colKeys.map((col, ci) => {
                const active = activeCell && activeCell.row === ri && activeCell.col === ci;
                const selected = isSelected(ri, ci);
                
                if (col.key === 'original') {
                  return (
                    <td
                      key={col.key}
                      style={{
                        ...tdStyle,
                        background: '#f1f3f9',
                        padding: '6px 8px',
                        fontSize: '11px',
                        verticalAlign: 'middle',
                        color: '#526484',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.originalLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                          <Spinner
                            animation="border"
                            size="sm"
                            role="status"
                            style={{ width: '14px', height: '14px', marginRight: '6px', color: '#5e72e4' }}
                          />
                          <span style={{ color: '#8094ae', fontSize: '10px' }}>Loading...</span>
                        </div>
                      ) : (
                        row.original || '-'
                      )}
                    </td>
                  );
                }

                return (
                  <td
                    key={col.key}
                    style={{
                      ...tdStyle,
                      background: selected
                        ? '#d0e8ff'
                        : active
                        ? '#fff'
                        : ri % 2 === 0
                        ? '#fff'
                        : '#f8f9ff',
                      outline: active ? '2px solid #5e72e4' : 'none',
                      outlineOffset: '-2px',
                    }}
                    onMouseDown={() => {
                      setSelStart({row: ri, col: ci});
                      setSelEnd({row: ri, col: ci});
                      setSelecting(true);
                    }}
                    onMouseEnter={() => {
                      if (selecting) setSelEnd({row: ri, col: ci});
                    }}
                    onMouseUp={() => {
                      setSelecting(false);
                      if (selStart && selEnd && selStart.col === selEnd.col && selStart.row !== selEnd.row) {
                        const colKey = colKeys[selStart.col].key;
                        if (colKey !== 'po' && colKey !== 'original') {
                          const startVal = rows[selStart.row][colKey] ?? '';
                          const minR = Math.min(selStart.row, selEnd.row);
                          const maxR = Math.max(selStart.row, selEnd.row);
                          const newRows = rows.map((row, ri) => {
                            if (ri >= minR && ri <= maxR) {
                              if (row.po && String(row.po).trim() !== '') {
                                return { ...row, [colKey]: startVal };
                              }
                            }
                            return row;
                          });
                          onChange(newRows);
                        }
                      }
                    }}
                  >
                    <input
                      data-r={ri}
                      data-c={ci}
                      value={row[col.key] ?? ''}
                      onChange={(e) => handleCellChange(ri, col.key, e.target.value)}
                      onFocus={() => {
                        setActiveCell({row: ri, col: ci});
                        setSelStart({row: ri, col: ci});
                        setSelEnd({row: ri, col: ci});
                      }}
                      onBlur={() => {
                        setActiveCell(null);
                        const cleaned = rows.filter((r) =>
                          colKeys.some((c) => c.key !== 'original' && String(r[c.key] ?? '').trim() !== '')
                        );
                        if (cleaned.length !== rows.length) {
                          onChange(cleaned.length > 0 ? cleaned : rows.slice(0, 1));
                        }
                      }}
                      onKeyDown={(e) => handleKeyDown(e, ri, ci)}
                      style={cellInput}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Estilos ─────────────────────────────────────────────────────────────────

const thStyle = {
  background: '#f5f6fa',
  color: '#526484',
  fontWeight: 700,
  fontSize: '12px',
  padding: '6px 8px',
  borderBottom: '2px solid #e5e9f2',
  borderRight: '1px solid #e5e9f2',
  textAlign: 'left',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const thRowNum = {
  ...thStyle,
  width: '36px',
  textAlign: 'center',
  color: '#aab1c5',
};

const tdStyle = {
  padding: 0,
  borderRight: '1px solid #e5e9f2',
  borderBottom: '1px solid #e5e9f2',
  position: 'relative',
};

const tdRowNum = {
  textAlign: 'center',
  fontSize: '11px',
  color: '#aab1c5',
  background: '#f5f6fa',
  borderRight: '2px solid #e5e9f2',
  borderBottom: '1px solid #e5e9f2',
  padding: '0 4px',
  userSelect: 'none',
};

const cellInput = {
  width: '100%',
  height: '30px',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: '0 8px',
  fontSize: '12px',
  fontFamily: 'inherit',
  cursor: 'cell',
};

export default ExcelGrid;
